import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Form, Input, Row } from 'antd'

import B2becCustomSelect from '@/components/B2becCustomSelect'
import { useAppSelector } from '@/store/hook'
import {
  selectAddressScheme,
  selectShouldHideHouseNumberEntryField,
} from '@/store/selectors/configSelector'
import { useDeviceDetect } from '@/hooks'
import useRegion from '@/hooks/useRegion'
import { ADDRESS_SCHEME_TYPES } from '@/libs/constants/configs'
import { FIELDS_MAX_LENGTH } from '@/libs/constants/formValidateConfig'
import { validateZipCode } from '@/libs/utils/validatorUtils'

const { TextArea } = Input

interface B2becAddressFormProps {
  id?: string
  disabled?: boolean
}

const B2becAddressForm = ({
  id: propName,
  disabled,
}: B2becAddressFormProps) => {
  const { t } = useTranslation()
  const { isLargeScreen } = useDeviceDetect()

  const { isShowRegion, regions } = useRegion()

  const shouldHideHouseNumber = useAppSelector(
    selectShouldHideHouseNumberEntryField,
  )
  const addressScheme = useAppSelector(selectAddressScheme)
  const isUSAAddressScheme = addressScheme === ADDRESS_SCHEME_TYPES.USA

  const mergedName = useCallback(
    (name) => {
      if (propName !== undefined) {
        return [propName, name]
      }

      return name
    },
    [propName],
  )

  return (
    <>
      <Form.Item
        className="custom-form-item"
        label={t('form.label.name')}
        name={mergedName('name')}
        rules={[
          { required: true },
          { type: 'string', max: FIELDS_MAX_LENGTH.name },
        ]}
      >
        <Input disabled={disabled} placeholder={t('form.placeHolder.name')} />
      </Form.Item>

      <Form.Item
        className="custom-form-item"
        label={t('form.label.extraText')}
        name={mergedName('name2')}
      >
        <TextArea placeholder={t('form.placeHolder.extraText')} />
      </Form.Item>

      <Row gutter={24}>
        <Col flex="auto">
          <Form.Item
            className="custom-form-item"
            label={t('form.label.street')}
            name={mergedName('street')}
            rules={[
              { required: true },
              { type: 'string', max: FIELDS_MAX_LENGTH.street },
            ]}
          >
            <Input
              disabled={disabled}
              placeholder={t('form.placeHolder.street')}
            />
          </Form.Item>
        </Col>
        {!shouldHideHouseNumber && (
          <Col lg={8} span={24}>
            <Form.Item
              className="custom-form-item"
              label={t('form.label.houseNumber')}
              name={mergedName('houseNumber')}
              rules={[
                { required: true },
                { type: 'string', max: FIELDS_MAX_LENGTH.houseNumber },
              ]}
            >
              <Input
                disabled={disabled}
                placeholder={t('form.placeHolder.houseNumber')}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Form.Item
        className="custom-form-item"
        label={t('form.label.street4')}
        name={mergedName('street4')}
        rules={[{ type: 'string', max: FIELDS_MAX_LENGTH.street4 }]}
      >
        <Input
          disabled={disabled}
          placeholder={t('form.placeHolder.street4')}
        />
      </Form.Item>
      <Row gutter={24}>
        <Col lg={{ span: 8 }} order={isUSAAddressScheme ? 2 : 0} span={24}>
          <Form.Item
            className="custom-form-item"
            label={t('form.label.zip')}
            name={mergedName('zip')}
            rules={[
              { required: true },
              ({ getFieldValue }) => {
                const country = getFieldValue(mergedName('country'))
                return validateZipCode(country)
              },
            ]}
          >
            <Input
              disabled={disabled}
              placeholder={t('form.placeHolder.zip')}
            />
          </Form.Item>
        </Col>
        {isShowRegion && (
          <Col lg={{ span: 8 }} order={1} span={24}>
            <Form.Item
              className="custom-form-item"
              label={t('form.label.region')}
              name={mergedName('region')}
              rules={[{ required: true }]}
            >
              <B2becCustomSelect
                disabled={disabled}
                options={regions}
                placeholder={t('form.placeHolder.region')}
              />
            </Form.Item>
          </Col>
        )}
        <Col
          lg={{ span: isShowRegion ? 8 : 16 }}
          order={isUSAAddressScheme ? 0 : 2}
          span={24}
        >
          <Form.Item
            className="custom-form-item"
            label={t('form.label.city')}
            name={mergedName('city')}
            rules={[
              { required: true },
              { type: 'string', max: FIELDS_MAX_LENGTH.city },
            ]}
          >
            <Input
              disabled={disabled}
              placeholder={t('form.placeHolder.city')}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        className="custom-form-item custom-form-item--disabled"
        label={t('form.label.country')}
        name={mergedName('country')}
        style={{ width: isLargeScreen ? 320 : 'unset' }}
      >
        <Input disabled />
      </Form.Item>
    </>
  )
}

export default B2becAddressForm
